import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part } from '../body';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import {
  domeLight,
  type Ellipse,
  fromPath,
  fromSegment,
  type HeadPicture,
  inEllipse,
  mask,
  type SetFlair,
  toneOf,
} from './mythic';

/**
 * The Kitsune set (ADR-0160, mythic): a white fox's mask, a shrine robe with a fan of tails, and the Foxfire gun.
 *
 * The head is a fox spirit's: a mane of white fur with two tall ears (pink inside, red at their tips), and over the
 * face a porcelain mask rimmed in gold, its snout to the right: narrow smiling eyes under red lids, a red mark on the brow, whiskers painted on the cheeks, the mask's cord and tassel in the team's colour down the cheek. The
 * robe is a white kimono crossed over a red collar, wide sleeves cuffed in red, the team's obi tied with a gold cord.
 * Its flair is what a fox spirit has: five tails fanned out behind (each a chain of links: they lag a step, swing back
 * and sway on their own), foxfire in the team's light circling it, cherry petals falling round it.
 *
 * Rubric (`assetc mythic` at 5× and 1:1 on grass, lane and night, both teams, idle, run and dead; the admin app's Skins
 * page, both previews; the `mythic-kitsune` playtest: the Store, PLAY's stage, a round at 1920×1080): mask S5 R5 P4 L4
 * C4 A4 F4, robe S4 R4 P4 L4 C4 F4. Still weak: white fur on a white mask leans on the mask's gold rim to part them; the
 * robe's collar is behind the gun but for its top; the tails are squares on the art grid, feathers more than fur at 5×.
 */

// --- The fox's head --------------------------------------------------------------------------------------------------

const FUR = [
  ['w', 0.55],
  ['5', 0.05],
  ['4', -0.4],
] as const;
/** From the head's middle (u forward, v down): the mane, tufts of fur at its cheeks. */
const MANE: Ellipse = [0, 0.5, 21.5, 16.5];
const TUFTS: readonly Ellipse[] = [
  [-20, 9, 4.5, 3],
  [-16.5, 14, 4.5, 2.6],
  [19, 11.5, 4, 2.6],
];
/** The ears: [tip u, tip v, base u, base v, half-base], the back one and the front one. */
const EARS: readonly (readonly [number, number, number, number, number])[] = [
  [-15, -25.5, -11, -11, 6.8],
  [12.5, -25.8, 9, -11.5, 6.2],
];
/** The mask over the face, and its snout. */
const FACE: Ellipse = [4, 3.5, 15, 12.5];
const SNOUT: Ellipse = [13.5, 9.5, 7.6, 5.4];
/** The eyes' middles, near and far, each half as wide as this, and how its outer corner lifts. */
const EYES: readonly (readonly [number, number, number, number])[] = [
  [-1.5, 2, 4.2, 0.38],
  [10.8, 2, 3.2, -0.38],
];

/** Where (u, v) is on an ear: how far down from its tip (0..1) and how far across (-1 back … 1 front); or null. */
function onEar(u: number, v: number): { t: number; across: number } | null {
  for (const [tu, tv, bu, bv, half] of EARS) {
    const t = (v - tv) / (bv - tv);
    if (t < 0 || t > 1.25) continue;
    const mid = tu + (bu - tu) * t;
    const w = half * Math.min(1, t) ** 0.85;
    if (Math.abs(u - mid) <= w) return { t, across: (u - mid) / Math.max(w, 0.5) };
  }
  return null;
}

const inMask = (u: number, v: number, grow = 0) =>
  inEllipse(u, v, [FACE[0], FACE[1], FACE[2] + grow, FACE[3] + grow]) <= 1 ||
  inEllipse(u, v, [SNOUT[0], SNOUT[1], SNOUT[2] + grow, SNOUT[3] + grow]) <= 1;

const fox: HeadPicture = (u, v, lx, ly) => {
  const inMane = inEllipse(u, v, MANE) <= 1 || TUFTS.some((t) => inEllipse(u, v, t) <= 1);
  if (inMask(u, v)) {
    // The mask's gold rim, lit where it faces the light.
    if (!inMask(u, v, -1.3)) return (u - FACE[0]) * lx + (v - FACE[1]) * ly > 0 ? 'y' : 'Y';
    for (const [eu, ev, half, lift] of EYES) {
      const du = u - eu;
      if (Math.abs(du) > half) continue;
      // A narrow smiling eye: a black crescent, its outer corner lifted, a red lid painted over it.
      const line = ev + du * lift - (1 - (du / half) ** 2) * 1.8;
      if (v >= line && v < line + 2.3) return 'k';
      if (v >= line - 1.4 && v < line && Math.abs(du) < half - 0.6) return 'r';
    }
    // The brow's mark: a flame of three petals.
    if (Math.abs(u - 5) + Math.abs(v + 6) * 0.55 < 2.2) return 'r';
    if (Math.abs(u - 1.2) + Math.abs(v + 4.6) * 0.7 < 1.3 || Math.abs(u - 8.8) + Math.abs(v + 4.6) * 0.7 < 1.3)
      return 'r';
    // Whiskers painted on each cheek.
    for (const [a, b, c, d] of [
      [-8, 7.2, -3.5, 8.2],
      [-7.5, 10, -3.5, 10.6],
      [15.5, 4.6, 18.6, 3.9],
    ] as const)
      if (fromSegment(u, v, a, b, c, d) < 0.62) return 'r';
    // Its nose, and the line of its mouth running back from it.
    if (inEllipse(u, v, [19.3, 9.2, 1.7, 1.5]) <= 1) return 'k';
    if (fromPath(u, v, [18.5, 11.5, 15, 13.2, 11, 12.4]) < 0.6) return 'N';
    return domeLight(u, v, FACE, lx, ly) < -0.12 ? '5' : 'w';
  }
  const ear = onEar(u, v);
  if (ear && !(inMane && ear.t > 1)) {
    if (ear.t < 0.3) return ear.across * lx > -0.1 ? 'r' : 'R';
    // Pink inside, the fur's rim round it, lit on the side toward the light.
    if (ear.t > 0.42 && Math.abs(ear.across) < 0.52) return ear.across * lx > 0.1 ? 'i' : 'p';
    return ear.across * lx > -0.15 ? 'w' : '5';
  }
  if (!inMane) return null;
  // The mask's cord down the cheek: a gold bead, the team's tassel.
  if (u >= -13.5 && u < -12.2 && v >= 2 && v < 9) return 'b';
  if (inEllipse(u, v, [-12.8, 9.6, 1.6, 1.5]) <= 1) return v < 9.4 ? 'y' : 'Y';
  if (Math.abs(u + 12.8) < 2.1 && v >= 11 && v < 17.5) return u < -13.8 ? 'c' : u > -11.9 ? 'B' : 'b';
  return toneOf(domeLight(u, v, MANE, lx, ly), FUR, '3');
};

// --- The shrine robe -------------------------------------------------------------------------------------------------

/**
 * The robe standing (the frame's x 7-52, y 42-55): a white kimono lit on its left, crossed left over right over a red
 * collar, a wide sleeve on each side cuffed in red (the far one in shade), the team's obi with its gold cord.
 */
function robe(): Part {
  const [x0, y0] = [7, 42];
  const c = new PixelCanvas(46, 14);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  for (let y = 43; y <= 50; y++)
    for (let x = 16; x <= 43; x++) at(x, y, x <= 17 ? 'w' : x >= 41 ? '4' : x >= 37 ? '5' : 'w');
  // The collar: the under-robe's red at the throat, the kimono crossed over it.
  for (let y = 43; y <= 50; y++) {
    const left = Math.round(23 + (y - 43) * 1.25);
    const right = Math.round(38 - (y - 43) * 0.9);
    for (let x = left; x <= right; x++) at(x, y, x <= left + 1 || x >= right - 1 ? 'r' : y === 43 ? '4' : '5');
    at(left - 1, y, '5');
  }
  for (const [xa, shade] of [
    [8, false],
    [43, true],
  ] as const)
    for (let y = 44; y <= 54; y++)
      for (let x = xa; x <= xa + 8; x++) {
        if (y === 44 && (x === xa || x === xa + 8)) continue;
        const cuff = y >= 53;
        const lit = !shade && (x <= xa + 1 || y === 44);
        at(
          x,
          y,
          cuff ? (x >= xa + 6 || shade ? 'R' : 'r') : lit ? 'w' : x >= xa + 7 ? '4' : shade ? '5' : y > 50 ? '5' : 'w',
        );
      }
  for (let x = 16; x <= 43; x++) {
    at(x, 51, 'c');
    at(x, 52, 'b');
    at(x, 53, x % 4 === 0 ? 'y' : 'Y');
    at(x, 54, 'b');
    at(x, 55, 'B');
  }
  // The cord's knot.
  c.stamp(
    `
    .yy.
    yYYy
    .YY.
  `,
    21 - x0,
    52 - y0,
  );
  return [c, x0, y0];
}

/** The robe lying (right of the legs, under the head): the obi at the waist (left), the kimono crossed over its red collar. */
const DEAD_ROBE = grid(`
  ....55ww
  ...5wwww
  c5wwwwww
  bYwwrwww
  bywwwrww
  BYwwwwrw
  bYwwwwrw
  bywwwwr5
  bYwwwwr5
  BYwwwrw5
  bywwrww5
  bYwwwww5
  bYwwww55
  By5www55
  bY5www5.
  bY55w555
  b5555555
  ....rrRR
`);

/** White tabi under the team's trousers. */
const SHRINE_ROBE = (): Outfit => ({ torso: robe(), dead: [DEAD_ROBE, 24, 51], legs: { n: 'w', N: '5' } });

// --- Foxfire on every other gun --------------------------------------------------------------------------------------

/**
 * The Foxfire finish (the default gun is its own, `pollinator_kitsune`): white lacquer where the steel was, red lacquer
 * where the wood was, the brass kept gold; the darkest steps stay dark for the outline.
 */
const FOXFIRE = { '3': '4', '4': '5', '5': 'w', n: 'R', m: 'r', t: 'r', e: 'i' } as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

const WHITE = 0xffffff;
/** A tail root to tip: white fur, pink, a red tip. */
const TAIL = [WHITE, WHITE, WHITE, WHITE, WHITE, WHITE, 0xf6757a, 0xe43b44, 0xa22633] as const;
/** Foxfire's light, in the team's colour. */
const FOXLIGHT = ['teamLight', 'team', 'teamDark'] as const;

/** A tail of the fan: `k` of 5, from straight back (0) to nearly straight up (4), each swaying on its own. */
const tail = (k: number) =>
  ({
    kind: 'chain',
    at: { on: 'torso', x: 17 - k * 0.6, y: 55 - k * 1.2 },
    n: 8,
    seg: 3.5,
    dir: Math.PI * (1.02 + k * 0.115),
    curl: 0.075,
    stiff: 13,
    g: 40,
    drag: 0.03,
    sway: [0.13, 0.32 + k * 0.06, k * 0.21],
    w: [6, 11, 3],
    shape: 'dots',
    tones: TAIL,
    rim: 0x5a6988,
    layer: 'back',
  }) as const;

const FLAIR: SetFlair = {
  head: [
    // Foxfire circling: three flames in the team's light, behind it on the far side of their round, flakes rising off them.
    {
      kind: 'orbit',
      at: { on: 'torso', x: 30, y: 38 },
      rx: 33,
      ry: 9,
      tilt: -0.08,
      n: 3,
      period: 5.2,
      size: 5,
      tones: ['team', 'teamLight', WHITE],
      flame: [15, 4.6],
      glow: 4.4,
      bob: [2.5, 0.6],
      shed: { rate: 9, v: [-3, 3, -34, -18], g: -24, life: [0.25, 0.5], size: [2, 1], tones: FOXLIGHT },
    },
  ],
  outfit: [
    // The fan of tails, the highest first (it is behind the others).
    tail(4),
    tail(3),
    tail(2),
    tail(1),
    tail(0),
    // Cherry petals falling round it.
    {
      kind: 'emit',
      at: { on: 'torso', x: 30, y: 26 },
      box: [64, 44],
      rate: 2.4,
      v: [-12, -3, 7, 16],
      // Mostly carried along: they fall round it, a few trailing behind a run.
      carry: 0.8,
      g: 5,
      drag: 0.6,
      wobble: [4, 0.7],
      life: [1.5, 2.6],
      size: [2, 2],
      tones: [WHITE, 0xf6757a, 0xf6757a, 0xb55088],
      layer: 'front',
    },
  ],
  gun: [
    // A paper charm on a red cord under the stock, and the lantern's flame in the team's light.
    {
      kind: 'chain',
      at: { on: 'gun', x: 9, y: 27 },
      n: 4,
      seg: 2.4,
      dir: Math.PI / 2,
      stiff: 2.5,
      g: 520,
      drag: 0.08,
      w: [1, 1, 4],
      shape: 'dots',
      tones: [0xe43b44, 0xe43b44, 0xe43b44, WHITE],
      layer: 'gun',
    },
    {
      kind: 'flame',
      at: { on: 'gun', x: 32, y: 9 },
      h: 8,
      w: 2.4,
      tones: ['teamDark', 'team', 'teamLight'],
      speed: 2.4,
      blown: 0.03,
      layer: 'gun',
    },
    {
      kind: 'pulse',
      at: { on: 'gun', x: 32, y: 8 },
      r: [6, 8],
      alpha: [0.18, 0.34],
      period: 0.9,
      tone: 'team',
      layer: 'gun',
    },
  ],
};

/** The Kitsune set (ADR-0160). */
export const KITSUNE = drawnLater({ head: () => mask(fox), outfit: SHRINE_ROBE, gun: FOXFIRE, flair: FLAIR });
